import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePosts } from '../context/PostsContext';
import {
  getBranchFeedPosts,
  getDepartmentFeedPosts,
  maxPostCreatedAtMs,
} from '../utils/feedFilters';

function storageKey(userId: string, feed: 'branch' | 'department'): string {
  return `intranet:feedSeen:${userId}:${feed}`;
}

function readCursorMs(userId: string, feed: 'branch' | 'department'): number {
  const raw = localStorage.getItem(storageKey(userId, feed));
  if (raw === null) return Number.POSITIVE_INFINITY;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
}

const FEED_SEEN_EVENT = 'intranet:feedSeenUpdated';

function writeCursorMs(userId: string, feed: 'branch' | 'department', ms: number): void {
  localStorage.setItem(storageKey(userId, feed), String(ms));
  window.dispatchEvent(new CustomEvent(FEED_SEEN_EVENT));
}

/**
 * Reddit-style unread indicator for My Branch / My Department feeds.
 * Baselines on first load so existing posts don't all look "new".
 */
export function useFeedUnreadDots(): {
  branchUnread: boolean;
  departmentUnread: boolean;
  onBranchNavClick: () => void;
  onDepartmentNavClick: () => void;
} {
  const { user } = useAuth();
  const { posts } = usePosts();
  const location = useLocation();
  const [, setBump] = useState(0);
  const bump = useCallback(() => setBump((x) => x + 1), []);

  useEffect(() => {
    const fn = () => bump();
    window.addEventListener(FEED_SEEN_EVENT, fn as EventListener);
    return () => window.removeEventListener(FEED_SEEN_EVENT, fn as EventListener);
  }, [bump]);

  const feed = new URLSearchParams(location.search).get('feed');
  const isFeedBranch = location.pathname === '/' && feed === 'branch';
  const isFeedDepartment = location.pathname === '/' && feed === 'department';

  const branchPosts = useMemo(
    () => getBranchFeedPosts(posts, user?.branch),
    [posts, user?.branch],
  );
  const departmentPosts = useMemo(
    () => getDepartmentFeedPosts(posts, user?.branch, user?.department),
    [posts, user?.branch, user?.department],
  );

  const userId = user?.id;

  // First visit: seed cursor so historical posts don't show a dot.
  useEffect(() => {
    if (!userId || !posts.length) return;
    for (const kind of ['branch', 'department'] as const) {
      if (localStorage.getItem(storageKey(userId, kind)) !== null) continue;
      const list = kind === 'branch' ? branchPosts : departmentPosts;
      const m = maxPostCreatedAtMs(list);
      writeCursorMs(userId, kind, m > 0 ? m : Date.now());
    }
  }, [userId, posts.length, branchPosts, departmentPosts]);

  const markBranchSeen = useCallback(() => {
    if (!userId) return;
    const m = Math.max(maxPostCreatedAtMs(branchPosts), Date.now());
    writeCursorMs(userId, 'branch', m);
  }, [userId, branchPosts]);

  const markDepartmentSeen = useCallback(() => {
    if (!userId) return;
    const m = Math.max(maxPostCreatedAtMs(departmentPosts), Date.now());
    writeCursorMs(userId, 'department', m);
  }, [userId, departmentPosts]);

  // While viewing a feed, keep cursor at least as new as the newest visible post.
  useEffect(() => {
    if (!userId || !isFeedBranch) return;
    markBranchSeen();
  }, [userId, isFeedBranch, posts, markBranchSeen]);

  useEffect(() => {
    if (!userId || !isFeedDepartment) return;
    markDepartmentSeen();
  }, [userId, isFeedDepartment, posts, markDepartmentSeen]);

  const branchCursor = userId ? readCursorMs(userId, 'branch') : Number.POSITIVE_INFINITY;
  const departmentCursor = userId ? readCursorMs(userId, 'department') : Number.POSITIVE_INFINITY;

  const branchUnread =
    !!userId &&
    branchPosts.some((p) => new Date(p.createdAt).getTime() > branchCursor);

  const departmentUnread =
    !!userId &&
    departmentPosts.some((p) => new Date(p.createdAt).getTime() > departmentCursor);

  return {
    branchUnread,
    departmentUnread,
    onBranchNavClick: markBranchSeen,
    onDepartmentNavClick: markDepartmentSeen,
  };
}
